import { CommonModule, FormatWidth, getLocaleDateFormat } from '@angular/common';
import { Component, effect, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { LayoutService } from '../../layout/layout.service';
import { ApiPurpleFitCompleteRegistrationRequest, ApiPurpleFitRegisterRequest, ApiPurpleFitUpdateUserInfoRequest, PRPVUserFull } from '../../../core-modules/sdk';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { PurpleFormComponent, PurpleFormFieldsGroup, PurpleGetPurpleSelectByIdRequest, PurpleGetPurpleSelectByNameRequest, PurpleSimpleButtonComponent, PurpleSimpleNotificationService } from '@purple-lib/purple-ui-widgets';
import { PurpleTranslationPipe, PurpleTranslationService } from '@purple-lib/purple-translations-core';
import { PurpleUserService } from '@purple-lib/purple-users-core';
import { Helper, PurpleBreakpointsService, PurpleDateExtensions } from '@purple-lib/purple-utilities';
import { ApiPolicyCheck, PurplePolicyFormComponent } from '@purple-lib/purple-policies-core';
import { PurpleEditableImageDirective } from '@purple-lib/purple-edit-medias';
import { PurpleEditableTextDirective } from '@purple-lib/purple-edit-translations';
import { AddressManagerService } from '@purple-lib/purple-addresses-core';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzIconModule, provideNzIconsPatch } from 'ng-zorro-antd/icon';
import { ArrowLeftOutline } from "@ant-design/icons-angular/icons";
import { PurpleFitCoreTranslations } from '../../../core-modules/constants/purple-fit-core-translations';
import { environment } from '../../../environments/default/environment';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['../authentication.scss', './register.component.scss'],
  imports: [CommonModule, FormsModule, PurpleTranslationPipe, PurpleFormComponent, PurpleEditableTextDirective, PurpleSimpleButtonComponent, NzTabsModule,
    PurplePolicyFormComponent, PurpleEditableImageDirective, NzDatePickerModule, NzIconModule],
  providers: [provideNzIconsPatch([ArrowLeftOutline])]
})
export class RegisterComponent implements OnInit {
  protected readonly T = PurpleFitCoreTranslations;

  constructor(@Inject(LOCALE_ID) private locale: string, private fb: FormBuilder,
    public breakSvc: PurpleBreakpointsService,
    private addSvc: AddressManagerService,
    private userSvc: PurpleUserService<PRPVUserFull, ApiPurpleFitRegisterRequest, ApiPurpleFitCompleteRegistrationRequest, ApiPurpleFitUpdateUserInfoRequest>,
    private router: Router,
    public tsvc: PurpleTranslationService, private layoutSvc: LayoutService, private route: ActivatedRoute,
    private psNotSvc: PurpleSimpleNotificationService) {
    this.validateForm = this.fb.group({})

    effect(() => {
      const device = this.breakSvc.breakPointDevice();
      if (device == "mobile" || device == "tablet") {
        this.purpleFormFieldGroups = [
          {
            fieldGroupNumber: 1,
            fieldGroupBootstrapColumn: 12,
            fieldGroupPaddingLeft: false,
            fieldGroupPaddingRight: false,
            formFieldFormGroup: this.validateForm,
            formFields: [
              {
                fieldType: 'input-text',
                fieldControlName: 'firstname',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_firstname, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'lastname',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_lastname, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'email',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_email, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldCustomValidators: [Validators.email],
                fieldCustomValidatorErrors: [{ errorId: "email", text: this.tsvc.getTranslationValue(this.T.invalid_email_validator_error_text) }]
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'password',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_password, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [{ id: 'isPassword', value: true }],
                fieldCustomValidators: [this.validatePassword],
                fieldCustomValidatorErrors: [{ errorId: "format", text: this.tsvc.getTranslationValue(this.T.invalid_email_validator_error_text) }],
                fieldShowInfo: true,
                fieldInfoText: this.tsvc.getTranslationValue(this.T.REGISTER.register_page_form_password_info, [])
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'checkPassword',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_check_password, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldCustomValidators: [this.confirmationValidator],
                fieldCustomValidatorErrors: [{ errorId: "match", text: this.tsvc.getTranslationValue(this.T.invalid_email_match_validator_error_text) }],
                fieldOptions: [{ id: 'isPassword', value: true }]
              },
              {
                fieldType: 'date',
                fieldControlName: 'birthdate',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_birthdate, []),
                fieldPlaceholder: " ",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'dateFormat', value: getLocaleDateFormat(this.locale, FormatWidth.Medium) },
                  { id: 'showTime', value: false }
                ]
              },
              {
                fieldType: 'select-local',
                fieldControlName: 'gender',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_gender, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'isMultiple', value: false },
                  { id: 'showArrow', value: true },
                  {
                    id: 'options', value: [
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_gender_man, []),
                        value: "M"
                      },
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_gender_female, []),
                        value: "F"
                      },
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_other, []),
                        value: "O"
                      }
                    ]
                  }
                ]
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'taxid',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_vatid, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false
              },
              {
                fieldType: 'select',
                fieldControlName: 'address',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_address, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'searchFunction', value: this.addressSearchFunction },
                  { id: 'searchByIdFunction', value: this.addressSearchIdFunction },
                  { id: 'refreshFieldNumber', value: 0 },
                ]
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'phone',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_phone, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldAffix: {
                  fieldControlName: "phonePrefix",
                  fieldPlaceholder: this.tsvc.getTranslationValue(this.T.register_page_form_phone_prefix_placeholder, []),
                  fieldType: "select-local",
                  fieldWidth: "90px",
                  fieldIsRequired: true,
                  fieldValue: environment.DEFAULT_PHONE_PREFIX,
                  fieldOptions: [
                    { id: 'type', value: 'default' },
                    { id: 'showArrow', value: true },
                    {
                      id: 'options', value: this.phonePrefixes
                    }
                  ]
                },
              },
              {
                fieldType: 'textarea',
                fieldControlName: 'injuries',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_injuries, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: false,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [{ id: 'rowNumber', value: 4 }]
              },
              // {
              //   fieldType: 'input-text',
              //   fieldControlName: 'promo',
              //   fieldBootstrapColumn: 12,
              //   fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_promo_code, []),
              //   fieldPlaceholder: "",
              //   fieldValue: undefined,
              //   fieldIsRequired: false,
              //   fieldIsDisabled: false,
              //   fieldPaddingLeft: false,
              //   fieldPaddingRight: false
              // }
            ]
          }
        ]
      } else {
        this.purpleFormFieldGroups = [
          {
            fieldGroupNumber: 1,
            fieldGroupBootstrapColumn: 12,
            fieldGroupPaddingLeft: false,
            fieldGroupPaddingRight: false,
            formFieldFormGroup: this.validateForm,
            formFields: [
              {
                fieldType: 'input-text',
                fieldControlName: 'firstname',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_firstname, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: true
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'lastname',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_lastname, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: true,
                fieldPaddingRight: false
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'email',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_email, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: true,
                fieldCustomValidators: [Validators.email],
                fieldCustomValidatorErrors: [{ errorId: "email", text: this.tsvc.getTranslationValue(this.T.invalid_email_validator_error_text) }]
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'taxid',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_vatid, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: true,
                fieldPaddingRight: false
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'password',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_password, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: true,
                fieldOptions: [{ id: 'isPassword', value: true }],
                fieldCustomValidators: [this.validatePassword],
                fieldCustomValidatorErrors: [{ errorId: "format", text: this.tsvc.getTranslationValue(this.T.invalid_email_validator_error_text) }],
                fieldShowInfo: true,
                fieldInfoText: this.tsvc.getTranslationValue(this.tsvc.getTranslationValue(this.T.REGISTER.register_page_form_password_info))

              },
              {
                fieldType: 'input-text',
                fieldControlName: 'checkPassword',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_check_password, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: true,
                fieldPaddingRight: false,
                fieldCustomValidators: [this.confirmationValidator],
                fieldCustomValidatorErrors: [{ errorId: "match", text: this.tsvc.getTranslationValue(this.T.invalid_email_match_validator_error_text) }],
                fieldOptions: [{ id: 'isPassword', value: true }]
              },
              {
                fieldType: 'date',
                fieldControlName: 'birthdate',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_birthdate, []),
                fieldPlaceholder: " ",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'dateFormat', value: getLocaleDateFormat(this.locale, FormatWidth.Medium) },
                  { id: 'showTime', value: false }
                ]
              }
            ]
          }
        ]

        this.purpleFormFieldGroups2 = [
          {
            fieldGroupNumber: 1,
            fieldGroupBootstrapColumn: 12,
            fieldGroupPaddingLeft: false,
            fieldGroupPaddingRight: false,
            formFieldFormGroup: this.validateForm,
            formFields: [
              {
                fieldType: 'select-local',
                fieldControlName: 'gender',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_gender, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: true,
                fieldOptions: [
                  { id: 'isMultiple', value: false },
                  { id: 'showArrow', value: true },
                  {
                    id: 'options', value: [
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_gender_man, []),
                        value: "M"
                      },
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_gender_female, []),
                        value: "F"
                      },
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_other, []),
                        value: "O"
                      }
                    ]
                  }
                ]
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'phone',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_phone, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: true,
                fieldPaddingRight: false,
                fieldAffix: {
                  fieldControlName: "phonePrefix",
                  fieldPlaceholder: this.tsvc.getTranslationValue(this.T.register_page_form_phone_prefix_placeholder, []),
                  fieldType: "select-local",
                  fieldWidth: "90px",
                  fieldIsRequired: true,
                  fieldValue: environment.DEFAULT_PHONE_PREFIX,
                  fieldOptions: [
                    { id: 'type', value: 'default' },
                    { id: 'showArrow', value: true },
                    {
                      id: 'options', value: this.phonePrefixes
                    }
                  ]
                },
              }, 
              {
                fieldType: 'select',
                fieldControlName: 'address',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_address, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'searchFunction', value: this.addressSearchFunction },
                  { id: 'searchByIdFunction', value: this.addressSearchIdFunction },
                  { id: 'refreshFieldNumber', value: 0 },
                ]
              },
              
              // {
              //   fieldType: 'input-text',
              //   fieldControlName: 'promo',
              //   fieldBootstrapColumn: 6,
              //   fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_promo_code, []),
              //   fieldPlaceholder: "",
              //   fieldValue: undefined,
              //   fieldIsRequired: false,
              //   fieldIsDisabled: false,
              //   fieldPaddingLeft: true,
              //   fieldPaddingRight: false
              // },
              {
                fieldType: 'textarea',
                fieldControlName: 'injuries',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_injuries, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: false,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [{ id: 'rowNumber', value: 2 }]
              }
            ]
          }
        ]
      }
    });
  }

  validateForm!: FormGroup;
  purpleFormFieldGroups: PurpleFormFieldsGroup[] | undefined;
  purpleFormFieldGroups2: PurpleFormFieldsGroup[] | undefined;
  phonePrefixes: { label: string, value: string }[] = Helper.getPhonePrefixes().map(m => { return { label: m, value: m } });
  currentTab: number = 0;

  buttonText: string = "";
  stepSubTitle: string = "";

  sub: Subject<void> = new Subject();
  isLoading: boolean = false;
  formPolicies: ApiPolicyCheck[] = []

  ngOnDestroy(): void {
    this.sub.next();
    this.sub.complete();
  }

  ngOnInit() {
    this.buttonText = this.tsvc.getTranslationValue(this.T.register_page_register_step_1_button, [])
    this.stepSubTitle = this.tsvc.getTranslationValue(this.T.register_page_desktop_step_1_form_subtitle, [])

    this.route.queryParams.subscribe(async params => {
      this.layoutSvc.setRedirectUrl(params, false);
    });
  }

  onTabChange(idx: number) {
    if (idx == 0) {
      this.buttonText = this.tsvc.getTranslationValue(this.T.register_page_register_step_1_button, [])
      this.stepSubTitle = this.tsvc.getTranslationValue(this.T.register_page_desktop_step_1_form_subtitle, [])
    } else {
      this.buttonText = this.tsvc.getTranslationValue(this.T.register_page_register_step_2_button, [])
      this.stepSubTitle = this.tsvc.getTranslationValue(this.T.register_page_desktop_step_2_form_subtitle, [])
    }
  }
  back() {
    this.currentTab = 0;
    this.onTabChange(this.currentTab);
  }

  async register() {
    if (this.currentTab == 0 && this.breakSvc.breakPointDevice() == 'desktop') {
      let isStepValid = true;

      this.purpleFormFieldGroups![0].formFields.forEach(element => {
        this.validateForm.controls[element.fieldControlName].markAsDirty();
        this.validateForm.controls[element.fieldControlName].updateValueAndValidity();

        if (isStepValid == true) {
          isStepValid = this.validateForm.controls[element.fieldControlName].valid;
        }
      });

      if (isStepValid) {
        this.currentTab++;
      }

    } else {
      this.isLoading = true;

      for (const i in this.validateForm.controls) {
        this.validateForm.controls[i].markAsDirty();
        this.validateForm.controls[i].updateValueAndValidity();
      }

      //const policies = this.polSvc.checkPolicyCheckBox("register-policy");

      if (this.validateForm.valid) {

        await this.userSvc.register({
          firstName: this.validateForm.controls["firstname"].value,
          lastName: this.validateForm.controls["lastname"].value,
          email: this.validateForm.controls["email"].value,
          password: this.validateForm.controls["password"].value,
          externalAddressId: this.validateForm.controls["address"].value,
          phonePrefix: this.validateForm.controls["phonePrefix"].value,
          phone: this.validateForm.controls["phone"].value,
          taxId: this.validateForm.controls["taxid"].value,
          birthDate: PurpleDateExtensions.toIsoStringPurple(this.validateForm.controls["birthdate"].value as Date),
          genderId: this.validateForm.controls["gender"].value,
          injuries: this.validateForm.controls["injuries"].value,
          policies: this.formPolicies,
          skipValidateRequest: false,
        }, () => {
          this.psNotSvc.showPurpleSimpleNotification({
            title: this.tsvc.getTranslationValue(this.T.register_ok_title, []),
            message: this.tsvc.getTranslationValue(this.T.register_ok_subtitle, []),
            buttonText: this.tsvc.getTranslationValue(this.T.register_ok_button, []),
            autoClose: true,
            buttonFunction: async () => {
              await this.router.navigate([this.tsvc.currentLanguage(), 'login'], { queryParams: { redirectUrl: this.layoutSvc.redirectUrl } });

            },
            cancelButtonFunction: async () => {
              await this.router.navigate([this.tsvc.currentLanguage(), 'login'], { queryParams: { redirectUrl: this.layoutSvc.redirectUrl } })
            },
          })
        });
      }
    }
    this.isLoading = false;
  }

  validatePassword = (control: AbstractControl): ValidationErrors | null => {
    const re = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})");
    const res = re.test(String(control.value));
    if (!res) {
      return { format: true }
    }
    return {}
  }

  confirmationValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return { required: true };
    } else if (control.value !== this.validateForm.controls['password'].value) {
      return { match: true };
    }
    return {};
  };

  //#region Address
  addressSearchFunction = (culture: string, args: PurpleGetPurpleSelectByNameRequest) => {
    return this.addSvc.getAddressAutocompleteByNameSelect(culture, args)
  }

  addressSearchIdFunction = (culture: string, args: PurpleGetPurpleSelectByIdRequest) => {
    return this.addSvc.getAddressAutocompleteByIdSelect(culture, args)
  }
  //#endregion

}
